import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useChatStore from '../store/useChatStore';
import { motion, AnimatePresence } from 'framer-motion';
import ViewToggle from '../components/ViewToggle';

const ConversationIcon = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 8H7M17 12H7" />
    </svg>
);
const TrashIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.34 9m-4.74 0l-.34-9m4.74-3H9m3.71-2H11.29a1 1 0 00-.99 1.01L10.32 4H13.68l.02-1.01a1 1 0 00-.99-1.01zM19 6H5m2 0h10l-.82 12.5a2 2 0 01-1.99 1.94H9.81a2 2 0 01-1.99-1.94L7 6z" /></svg>;
const SearchIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>;
const CalendarIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>;

const HistoryPage = () => {
    const { standaloneChats, deleteChat } = useChatStore();
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('conversations_view_mode') || 'grid');
    const navigate = useNavigate();

    React.useEffect(() => {
        localStorage.setItem('conversations_view_mode', viewMode);
    }, [viewMode]);

    const filteredChats = standaloneChats.filter(chat =>
        (chat.title || 'Untitled Conversation').toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatDate = (timestamp) => {
        if (!timestamp) return 'No date';
        const date = timestamp.toDate();
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        }).format(date);
    };

    return (
        <div className="pb-20 min-h-full bg-[var(--bg-main)] flex flex-col font-sans selection:bg-[var(--accent-primary)]/20 overflow-hidden">
            {/* Header Area */}
            <header className="px-4 md:px-8 py-8 md:py-14 max-w-7xl mx-auto w-full flex flex-col md:flex-row md:justify-between md:items-end gap-6">
                <div className="space-y-1">
                    <h1 className="text-3xl md:text-4xl font-light text-[var(--text-main)] tracking-tighter transition-all">
                        Conversations
                    </h1>
                    <p className="text-sm text-[var(--text-muted)] font-medium">
                        Manage and revisit your past conversations.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
                    <div className="relative w-full sm:w-80">
                        <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-dim)]" />
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] rounded-full text-[12px] focus:outline-none focus:ring-1 focus:ring-indigo-500/50 transition-all text-[var(--text-main)] placeholder-[var(--text-dim)] shadow-sm"
                        />
                    </div>
                    <ViewToggle mode={viewMode} onChange={setViewMode} />
                </div>
            </header>

            {/* Content Area */}
            <div className="px-4 md:px-8 max-w-7xl mx-auto w-full flex-1 overflow-y-auto custom-scrollbar">
                <AnimatePresence mode="popLayout">
                    {filteredChats.length > 0 ? (
                        <motion.div
                            layout
                            className={viewMode === 'grid'
                                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                                : "flex flex-col gap-3 mb-20"}
                        >
                            {filteredChats.map((chat) => (
                                <motion.div
                                    key={chat.id}
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className={viewMode === 'grid' ? "" : ""}
                                >
                                    {viewMode === 'grid' ? (
                                        <div className="group relative bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-2xl p-5 hover:border-[var(--border-strong)] transition-all hover:shadow-xl hover:-translate-y-1 h-full flex flex-col">
                                            <div
                                                onClick={() => navigate(`/chat/${chat.id}`)}
                                                className="cursor-pointer flex-1"
                                            >
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-400">
                                                        <ConversationIcon className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-bold text-[var(--text-main)] truncate text-base leading-tight">
                                                            {chat.title || 'Untitled Conversation'}
                                                        </h3>
                                                        <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)] mt-1 font-medium">
                                                            <CalendarIcon className="w-3 h-3" />
                                                            {formatDate(chat.lastActive || chat.createdAt)}
                                                        </div>
                                                    </div>
                                                </div>

                                                <p className="text-xs text-[var(--text-dim)] line-clamp-2 leading-relaxed mb-4">
                                                    {chat.lastMessage || 'No messages yet.'}
                                                </p>
                                            </div>

                                            <div className="flex items-center justify-between mt-auto pt-4 border-t border-[var(--border-subtle)]">
                                                <button
                                                    onClick={() => navigate(`/chat/${chat.id}`)}
                                                    className="text-[11px] font-bold text-indigo-400 hover:text-indigo-300 transition-colors uppercase tracking-widest pl-1"
                                                >
                                                    Open Conversation
                                                </button>
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (window.confirm('Delete this conversation forever?')) {
                                                            await deleteChat(chat.id);
                                                        }
                                                    }}
                                                    className="p-1.5 rounded-lg text-red-400/40 hover:text-red-400 hover:bg-red-500/10 transition-all"
                                                    title="Delete Conversation"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            onClick={() => navigate(`/chat/${chat.id}`)}
                                            className="group relative flex items-center justify-between p-3.5 bg-[var(--bg-secondary)] border border-[var(--border-subtle)] rounded-xl hover:border-[var(--border-medium)] transition-all duration-200 shadow-sm cursor-pointer pr-24"
                                        >
                                            <div className="flex items-center gap-4 flex-1 min-w-0">
                                                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-400 flex items-center justify-center shrink-0">
                                                    <ConversationIcon className="w-4 h-4" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="text-[14px] font-medium text-[var(--text-main)] truncate leading-tight group-hover:text-[var(--accent-primary)] transition-colors">
                                                        {chat.title || 'Untitled Conversation'}
                                                    </h3>
                                                    <p className="text-[11px] text-[var(--text-dim)] truncate mt-0.5">
                                                        {chat.lastMessage || 'No messages yet.'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-6 shrink-0">
                                                <div className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)] font-medium">
                                                    <CalendarIcon className="w-3 h-3 opacity-60" />
                                                    {formatDate(chat.lastActive || chat.createdAt)}
                                                </div>
                                                <span className="text-[10px] font-bold text-[var(--text-dim)] uppercase tracking-widest bg-[var(--bg-tertiary)]/50 px-2 py-0.5 rounded border border-[var(--border-subtle)] leading-none text-center min-w-[70px]">
                                                    Conversation
                                                </span>
                                            </div>

                                            <div className="absolute top-1/2 -translate-y-1/2 right-3 flex gap-1 items-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/chat/${chat.id}`);
                                                    }}
                                                    className="p-1.5 rounded-md text-indigo-400 hover:bg-indigo-500/10 transition-all"
                                                    title="Open Conversation"
                                                >
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                                                </button>
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (window.confirm('Delete this conversation forever?')) {
                                                            await deleteChat(chat.id);
                                                        }
                                                    }}
                                                    className="p-1.5 rounded-md text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-all"
                                                    title="Delete Conversation"
                                                >
                                                    <TrashIcon className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex flex-col items-center justify-center py-32 text-center"
                        >
                            <div className="w-20 h-20 rounded-full bg-[var(--bg-tertiary)] flex items-center justify-center mb-6 border border-[var(--border-subtle)] shadow-inner">
                                <ConversationIcon className="w-10 h-10 text-[var(--text-dim)] opacity-20" />
                            </div>
                            <h3 className="text-xl font-bold text-[var(--text-main)] mb-2">No conversations found</h3>
                            <p className="text-[var(--text-muted)] max-w-xs mx-auto text-sm leading-relaxed font-medium">
                                {searchQuery ? `We couldn't find any conversations matching "${searchQuery}"` : "You haven't started any conversations yet."}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default HistoryPage;
